import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Copy } from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip } from '@/components/data';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  Field, NumberField, FieldGroup, StyleSelect, LayoutAdvisory, CostSheet, fmt,
} from './Costing/EstimatorPanel';
import PhoneCosting from './Costing/PhoneCosting';

const BLANK_INPUTS = {
  style_code: 'STE',
  length_mm: 60, width_mm: 60, height_mm: 108,
  quantity: 100000,
  sheet_width_mm: 615, sheet_length_mm: 515, gsm: 240,
  allowances: {}, trims: {}, bleed: {}, gutters: {},
  cost_group: '', manual_units: {}, extra_rows: [],
  wastage_pct: 0, margin_pct: 0, force_orientation: null,
};

const TRIM_FIELDS = [
  ['gripper', 'Gripper'], ['side_lay', 'Side lay'],
  ['side_trim', 'Side trim'], ['tail_trim', 'Tail trim'],
];
const SPACING_FIELDS = [
  ['bleed', 'bleed_x', 'Bleed X'], ['bleed', 'bleed_y', 'Bleed Y'],
  ['gutters', 'gutter_x', 'Gutter X'], ['gutters', 'gutter_y', 'Gutter Y'],
];

export default function BoxCostingPage() {
  const isMobile = useIsMobile();
  const [styles, setStyles] = useState([]);
  const [rates, setRates] = useState([]);
  const [costGroups, setCostGroups] = useState([]);
  const [units, setUnits] = useState([]);
  const [inputs, setInputs] = useState(BLANK_INPUTS);
  const [result, setResult] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [saveDialog, setSaveDialog] = useState(false);
  const [meta, setMeta] = useState({ name: '', customer_name: '', product: '', notes: '' });
  const [newRate, setNewRate] = useState({ cost_group: '', process: '', rate: '', per: 1, unit: 'Sht', minimum: 0, remark: '' });

  const set = (patch) => setInputs(p => ({ ...p, ...patch }));
  const setNested = (group, key, value) =>
    setInputs(p => ({ ...p, [group]: { ...p[group], [key]: value } }));

  const loadRates = useCallback(async () => {
    try {
      const { data } = await api.get('/costing/rates');
      setRates(data.rates || []);
      setCostGroups(data.cost_groups || []);
      setUnits(data.units || []);
    } catch { toast.error('Could not load the rate master'); }
  }, []);

  const loadEstimates = useCallback(async () => {
    try {
      const { data } = await api.get('/costing/estimates');
      setEstimates(data.estimates || []);
    } catch { /* the list is a convenience, not a blocker */ }
  }, []);

  useEffect(() => {
    api.get('/costing/box-styles').then(({ data }) => setStyles(data.styles || []))
      .catch(() => toast.error('Could not load box styles'));
    loadRates();
    loadEstimates();
  }, [loadRates, loadEstimates]);

  // the estimator changes a trim by 2mm and watches the ups move; that only
  // works if the recompute follows the keystroke rather than a Calculate button
  useEffect(() => {
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post('/costing/compute', inputs);
        setResult(data);
      } catch (e) {
        setResult(null);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [inputs]);

  const style = useMemo(() => styles.find(s => s.code === inputs.style_code), [styles, inputs.style_code]);

  // the answer, on one row under the inputs — you change a trim above and read
  // the ups and the price here without moving your eyes down the page
  const summary = useMemo(() => {
    if (!result) return [];
    const { imposition: imp, geometry: geo, cost_sheet: cs } = result;
    const priced = cs?.rows?.length > 0;
    return [
      { key: 'blank', label: 'mm blank', value: `${fmt(geo.blank_width_mm, 1)} × ${fmt(geo.blank_length_mm, 1)}` },
      { key: 'ups', label: 'ups / sheet', value: `${imp.ups_across_width} × ${imp.ups_along_length} = ${imp.total_ups}`, tone: 'blue' },
      { key: 'sheets', label: 'sheets', value: fmt(imp.sheets_required) },
      { key: 'waste', label: 'sheet waste', value: `${fmt(imp.paper_waste_pct, 2)}%`, tone: imp.paper_waste_pct > 25 ? 'warn' : 'default' },
      { key: 'board', label: 'kg board', value: fmt(imp.board_weight_kg, 3) },
      priced && { key: 'total', label: 'total', value: `₹${fmt(cs.total, 2)}`, tone: 'accent' },
      priced && { key: 'per-unit', label: 'per unit', value: `₹${fmt(cs.cost_per_unit, 4)}` },
    ];
  }, [result]);

  const saveEstimate = async () => {
    if (!meta.name.trim()) { toast.error('Give the estimate a name'); return; }
    try {
      await api.post('/costing/estimates', { ...meta, inputs });
      toast.success('Estimate saved');
      setSaveDialog(false);
      loadEstimates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const startSimilar = async (id) => {
    try {
      const { data } = await api.post(`/costing/estimates/${id}/similar`);
      setInputs({ ...BLANK_INPUTS, ...data.inputs });
      setMeta({ name: data.name, customer_name: data.customer_name, product: data.product, notes: data.notes });
      toast.success('Started from that estimate');
    } catch { toast.error('Could not copy that estimate'); }
  };

  const addRate = async () => {
    if (!newRate.cost_group || !newRate.process) { toast.error('Cost group and process are required'); return; }
    try {
      await api.post('/costing/rates', { ...newRate, rate: parseFloat(newRate.rate) || 0 });
      toast.success('Rate added');
      setNewRate({ ...newRate, process: '', rate: '', remark: '' });
      loadRates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add the rate'); }
  };

  const removeRate = async (id) => {
    try {
      await api.delete(`/costing/rates/${id}`);
      loadRates();
    } catch { toast.error('Could not remove the rate'); }
  };

  const saveDialogNode = (
    <Dialog open={saveDialog} onOpenChange={setSaveDialog}>
      <DialogContent>
        <DialogHeader><DialogTitle>Save this estimate</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {[
            ['name', 'Estimate name *', 'e.g. Sharma Pharma 60×60×108'],
            ['customer_name', 'Customer', ''],
            ['product', 'Product', ''],
            ['notes', 'Notes', ''],
          ].map(([key, label, placeholder]) => (
            <div key={key}>
              <label className="text-xs font-medium text-slate-600">{label}</label>
              <Input value={meta[key]} placeholder={placeholder}
                onChange={e => setMeta(p => ({ ...p, [key]: e.target.value }))} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSaveDialog(false)}>Cancel</Button>
          <Button onClick={saveEstimate} className="bg-brand-blue hover:bg-brand-blue-dark text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // one column below lg; the same inputs, the same recompute, the same dialog
  if (isMobile) {
    return (
      <>
        <PhoneCosting
          styles={styles} style={style} inputs={inputs} set={set} setNested={setNested}
          result={result} summary={summary}
          trimFields={TRIM_FIELDS} spacingFields={SPACING_FIELDS}
          costGroups={costGroups} units={units}
          rates={rates} newRate={newRate} setNewRate={setNewRate} onAddRate={addRate} onRemoveRate={removeRate}
          estimates={estimates} onStartSimilar={startSimilar}
          onSave={() => setSaveDialog(true)}
        />
        {saveDialogNode}
      </>
    );
  }

  return (
    <Screen data-testid="box-costing-page">
      <CommandBar
        title="Box Costing"
        subtitle="Geometry, then imposition, then price — in that order"
        actions={[
          { key: 'save', label: 'Save Estimate', icon: Save, tone: 'accent', onClick: () => setSaveDialog(true) },
        ]}
      />

      {/* Everything you type, across the top. It used to be a single tall column
          beside the results, which meant the sheet settings scrolled out of view
          exactly while you were adjusting them against the imposition. */}
      <div className="shrink-0 rounded-lg border border-slate-200 bg-white p-2 shadow-card" data-testid="input-band">
        <div className="grid gap-x-4 gap-y-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.55fr)_minmax(0,0.75fr)]">
          <FieldGroup title="Box" aside={style?.name}>
            {/* the code and the style's full name only read as one value at full width */}
            <Field label="Style" className="col-span-full">
              <StyleSelect styles={styles} value={inputs.style_code} onChange={v => set({ style_code: v })} />
            </Field>
            <NumberField label="Length" suffix="mm" value={inputs.length_mm} onChange={v => set({ length_mm: v })} />
            <NumberField label="Width" suffix="mm" value={inputs.width_mm} onChange={v => set({ width_mm: v })} />
            <NumberField label="Height" suffix="mm" value={inputs.height_mm} onChange={v => set({ height_mm: v })} />
            <NumberField label="Quantity" suffix="pcs" value={inputs.quantity} onChange={v => set({ quantity: v })} />
          </FieldGroup>

          <FieldGroup title="Sheet, trims and spacing">
            <NumberField label="Sheet width" suffix="mm" value={inputs.sheet_width_mm} onChange={v => set({ sheet_width_mm: v })} />
            <NumberField label="Sheet length" suffix="mm" value={inputs.sheet_length_mm} onChange={v => set({ sheet_length_mm: v })} />
            <NumberField label="GSM" value={inputs.gsm} onChange={v => set({ gsm: v })} />
            {TRIM_FIELDS.map(([key, label]) => (
              <NumberField key={key} label={label} suffix="mm" value={inputs.trims[key] ?? ''}
                onChange={v => setNested('trims', key, v)} />
            ))}
            {SPACING_FIELDS.map(([group, key, label]) => (
              <NumberField key={key} label={label} suffix="mm" value={inputs[group][key] ?? ''}
                onChange={v => setNested(group, key, v)} />
            ))}
          </FieldGroup>

          <FieldGroup title="Pricing">
            <Field label="Cost group">
              <Select value={inputs.cost_group || ''} onValueChange={v => set({ cost_group: v })}>
                <SelectTrigger className="h-6 border-0 px-1.5 text-xs shadow-none" data-testid="cost-group-select">
                  <SelectValue placeholder="Pick a group" />
                </SelectTrigger>
                <SelectContent>
                  {costGroups.map(g => <SelectItem key={g} value={g} className="text-xs">{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <NumberField label="Wastage" suffix="%" value={inputs.wastage_pct || ''}
              onChange={v => set({ wastage_pct: v || 0 })} />
            <NumberField label="Margin" suffix="%" value={inputs.margin_pct || ''}
              onChange={v => set({ margin_pct: v || 0 })} />
          </FieldGroup>
        </div>

        {style?.allowances?.length > 0 && (
          <FieldGroup title="Allowances" aside="blank if derived from the size"
            className="mt-2 border-t border-slate-100 pt-2">
            {style.allowances.map(name => (
              <NumberField
                key={name}
                label={name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                suffix="mm"
                value={inputs.allowances[name] ?? ''}
                placeholder={result?.geometry?.allowances?.[name] ?? 'auto'}
                onChange={v => setNested('allowances', name, v)}
              />
            ))}
          </FieldGroup>
        )}

        {style && <p className="mt-1.5 text-[11px] text-slate-500">{style.description}</p>}
      </div>

      {result ? (
        <StatStrip stats={summary} data-testid="costing-summary" />
      ) : (
        <div className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-slate-400 shadow-card">
          Enter the box and the sheet — the summary fills itself in.
        </div>
      )}

      <div className="grid min-h-0 flex-1 gap-2.5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)_minmax(0,1fr)]">
        <Pane title="Imposition"
          aside={result ? `${result.imposition.usable_width_mm} × ${result.imposition.usable_length_mm} mm usable` : ''}>
          {result ? (
            <LayoutAdvisory
              result={result}
              forced={inputs.force_orientation}
              onRotate={() => set({
                force_orientation: inputs.force_orientation === 'rotated' ? 'upright' : 'rotated',
              })}
            />
          ) : (
            <p className="px-3 py-6 text-center text-xs text-slate-400">Enter the box and the sheet.</p>
          )}
        </Pane>

        <Pane title="Cost Sheet" aside="quantities come off the imposition">
          <CostSheet sheet={result?.cost_sheet} rateCount={rates.length} />
        </Pane>

        {/* The rate master sits beside the cost sheet rather than behind a tab:
            you cost against a rate, so you should be able to see — and fix — the
            rate you are costing against without losing the estimate you were
            part-way through building. */}
        <div className="flex min-h-0 flex-col gap-2.5">
          <Pane title="Rate master" aside={`${rates.length} rates`} className="min-h-0 flex-1">
            <div className="space-y-2 p-2">
              <p className="text-[11px] leading-relaxed text-slate-500">
                A rate is <span className="font-medium">cost group → process</span>, charged per block of units, with
                a minimum for short runs. The same process can be priced twice if the remark tells them apart.
              </p>

              <div className="grid gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-2"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(7rem, 1fr))' }}>
                <Input className="h-7 text-xs" placeholder="Cost group" value={newRate.cost_group}
                  onChange={e => setNewRate(p => ({ ...p, cost_group: e.target.value }))} />
                <Input className="h-7 text-xs" placeholder="Process" value={newRate.process}
                  onChange={e => setNewRate(p => ({ ...p, process: e.target.value }))} />
                <Input className="h-7 text-xs" type="number" placeholder="Rate" value={newRate.rate}
                  onChange={e => setNewRate(p => ({ ...p, rate: e.target.value }))} />
                <Input className="h-7 text-xs" type="number" placeholder="Per" value={newRate.per}
                  onChange={e => setNewRate(p => ({ ...p, per: parseFloat(e.target.value) || 1 }))} />
                <Select value={newRate.unit} onValueChange={v => setNewRate(p => ({ ...p, unit: v }))}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {units.map(u => <SelectItem key={u} value={u} className="text-xs">{u}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input className="h-7 text-xs" placeholder="Remark" value={newRate.remark}
                  onChange={e => setNewRate(p => ({ ...p, remark: e.target.value }))} />
                <Button size="sm" onClick={addRate} className="h-7 bg-brand-blue text-white hover:bg-brand-blue-dark">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add
                </Button>
              </div>

              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      {['Cost Group', 'Process', 'Rate', 'Per', 'Unit', 'Minimum', 'Remark', ''].map(h => (
                        <th key={h} className="px-2 py-1.5 text-left">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rates.length === 0 ? (
                      <tr><td colSpan={8} className="px-2 py-8 text-center text-slate-400">
                        No rates yet. Until there are, the cost sheet has nothing to price.
                      </td></tr>
                    ) : rates.map(r => (
                      <tr key={r.id} className="border-t border-slate-200">
                        <td className="px-2 py-1.5">{r.cost_group}</td>
                        <td className="px-2 py-1.5 font-medium">{r.process}</td>
                        <td className="px-2 py-1.5 tabular-nums">{r.rate}</td>
                        <td className="px-2 py-1.5 tabular-nums text-slate-400">{r.per}</td>
                        <td className="px-2 py-1.5">{r.unit}</td>
                        <td className="px-2 py-1.5 tabular-nums">{r.minimum || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-500">{r.remark}</td>
                        <td className="px-2 py-1.5 text-right">
                          <button onClick={() => removeRate(r.id)} className="text-red-400 hover:text-red-600">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Pane>

          <Pane title="Saved estimates" aside={`${estimates.length}`} collapsible defaultOpen={false}>
            <div className="overflow-x-auto p-2">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    {['Estimate', 'Customer', 'Product', 'Style', 'Qty', 'Ups', 'Margin', 'Total', 'Per unit', ''].map(h => (
                      <th key={h} className="px-2 py-1.5 text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {estimates.length === 0 ? (
                    <tr><td colSpan={10} className="px-2 py-8 text-center text-slate-400">No saved estimates yet.</td></tr>
                  ) : estimates.map(e => (
                    <tr key={e.id} className="border-t border-slate-200">
                      <td className="px-2 py-1.5 font-medium">{e.name}</td>
                      <td className="px-2 py-1.5">{e.customer_name}</td>
                      <td className="px-2 py-1.5">{e.product}</td>
                      <td className="px-2 py-1.5"><Badge variant="outline" className="font-mono text-[10px]">{e.style_code}</Badge></td>
                      <td className="px-2 py-1.5 tabular-nums">{e.quantity?.toLocaleString()}</td>
                      <td className="px-2 py-1.5 tabular-nums">{e.total_ups}</td>
                      <td className="px-2 py-1.5 tabular-nums">{e.margin_pct}%</td>
                      <td className="px-2 py-1.5 tabular-nums font-semibold">₹{e.total?.toLocaleString()}</td>
                      <td className="px-2 py-1.5 tabular-nums">₹{e.cost_per_unit}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => startSimilar(e.id)}>
                          <Copy className="mr-1 h-3 w-3" /> Similar to
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Pane>
        </div>
      </div>

      {saveDialogNode}
    </Screen>
  );
}
